import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {useDispatch} from "react-redux";
import {Card} from "../../../basic/card.tsx";
import {Button} from "../../../basic/button.tsx";
import {Input} from "../../../basic/input.tsx";
import {Label} from "../../../basic/label.tsx";
import {Separator} from "../../../basic/separator.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {
    useCancelEmailChangeMutation,
    useConfirmEmailChangeMutation,
    useGetCurrentUserQuery,
    useGetEmailChangeStatusQuery,
    useInitiateEmailChangeMutation,
    useUpdateProfileMutation
} from "../../../../store/generated/api.ts";
import type {VwpEmailChangeResponse} from "../../../../store/generated/api.ts";
import {toast} from "sonner";
import {setProfileUpdatePending} from "../../../../store/updatesSlice";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "../../../basic/alert-dialog.tsx";
import {reauthenticate} from "../../../../services";
import {PendingEmailVerification} from "./PendingEmailVerification.tsx";

const OPEN_STATUSES = ["PENDING", "AWAITING_VERIFICATION"];
const RESEND_COOLDOWN_MS = 60_000;

function isOpen(change?: VwpEmailChangeResponse): boolean {
    return !!change?.status && OPEN_STATUSES.includes(change.status);
}

function changeKey(change?: VwpEmailChangeResponse): string | null {
    return change?.status ? `${change.changeId}:${change.status}` : null;
}

export function AccountSettings() {
    const {t} = useTranslationWithKeys();
    const {handleApiError} = useErrorHandler();
    const dispatch = useDispatch();
    const {data, isLoading} = useGetCurrentUserQuery();
    const user = data?.user;
    const [updateProfile, {isLoading: isUpdating}] = useUpdateProfileMutation();
    const [initiateEmailChange, {isLoading: isInitiating}] = useInitiateEmailChangeMutation();
    const [cancelEmailChange, {isLoading: isCancelling}] = useCancelEmailChangeMutation();
    const [confirmEmailChange, {isLoading: isConfirming}] = useConfirmEmailChangeMutation();
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [pendingEmail, setPendingEmail] = useState("");
    const [resendAvailableAt, setResendAvailableAt] = useState(0);

    // restores the pending state when returning mid-verification-window
    const [pollChange, setPollChange] = useState(false);
    const {data: changeStatus, isError: statusError} = useGetEmailChangeStatusQuery(undefined, {
        pollingInterval: pollChange ? 3000 : 0
    });

    useEffect(() => {
        setPollChange(isOpen(changeStatus));
    }, [changeStatus]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: {errors, isDirty}
    } = useForm<{ firstName: string; lastName: string; email: string }>({
        mode: "onBlur"
    });

    const watchedEmail = watch("email");
    const openChange = isOpen(changeStatus) ? changeStatus : undefined;

    useEffect(() => {
        if (user && !openChange) {
            reset({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || ""
            });
        }
    }, [user, reset, openChange]);

    // Only react to terminal transitions observed in this session; a terminal
    // change already present on first load must not retrigger (login/logout loop).
    const initialChangeKey = useRef<string | null | undefined>(undefined);
    const handledChangeKey = useRef<string | null>(null);

    const handleTerminalChange = (change?: VwpEmailChangeResponse) => {
        const key = changeKey(change);
        if (!key || isOpen(change) || key === initialChangeKey.current || key === handledChangeKey.current) {
            return;
        }
        handledChangeKey.current = key;

        if (change?.status === "COMPLETED") {
            toast.success(t('settings.emailChange.verified'));
            reauthenticate();
        } else if (change?.status === "ROLLED_BACK" || change?.status === "CANCELLED") {
            toast.info(t('settings.emailChange.rolledBack'));
        }
    };

    useEffect(() => {
        if (changeStatus === undefined) {
            return;
        }
        if (initialChangeKey.current === undefined) {
            initialChangeKey.current = changeKey(changeStatus);
            return;
        }
        handleTerminalChange(changeStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeStatus?.changeId, changeStatus?.status]);

    const onSubmit = async (data: { firstName: string; lastName: string; email: string }) => {
        const emailChanged = data.email !== user?.email;

        if (emailChanged) {
            setPendingEmail(data.email);
            setEmailDialogOpen(true);
            return;
        }

        try {
            dispatch(setProfileUpdatePending(true));

            await updateProfile({
                vwpAuthUserUpdateRequest: {
                    firstName: data.firstName,
                    lastName: data.lastName
                }
            }).unwrap();

            toast.success(t('settings.profileUpdateSuccess'));
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('settings.profileUpdateError')});
        } finally {
            dispatch(setProfileUpdatePending(false));
        }
    };

    const handleInitiateEmailChange = async () => {
        setEmailDialogOpen(false);
        await startEmailChange(pendingEmail);
    };

    const startEmailChange = async (newEmail: string) => {
        try {
            await initiateEmailChange({
                vwpEmailChangeRequest: {newEmail}
            }).unwrap();

            setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
            toast.success(t('settings.emailChange.initiated'));
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('settings.emailChange.initiateError')});
        }
    };

    const handleCancelEmailChange = async () => {
        try {
            await cancelEmailChange().unwrap();
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('settings.emailChange.cancelError')});
        }
    };

    const handleResendEmailChange = async () => {
        // the license server rejects a second initiate while one is open — resend = cancel + re-initiate
        try {
            await cancelEmailChange().unwrap();
            await startEmailChange(openChange?.newEmail ?? pendingEmail);
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('settings.emailChange.resendError')});
        }
    };

    const handleConfirmEmailChange = async (code: string) => {
        try {
            const result = await confirmEmailChange({vwpEmailChangeConfirmRequest: {code}}).unwrap();
            handleTerminalChange(result);
        } catch (error: any) {
            handleApiError(error, {fallbackMessage: t('settings.emailChange.invalidCode')});
        }
    };

    if (isLoading) {
        return (
            <Card className="p-6 shadow-sm border-border/50">
                <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
            </Card>
        );
    }

    if (openChange) {
        return (
            <PendingEmailVerification
                newEmail={openChange.newEmail}
                expiresAt={openChange.expiresAt}
                statusError={statusError}
                cancelling={isCancelling}
                confirming={isConfirming}
                resending={isCancelling || isInitiating}
                resendAvailableAt={resendAvailableAt}
                onConfirm={handleConfirmEmailChange}
                onResend={handleResendEmailChange}
                onCancel={handleCancelEmailChange}
            />
        );
    }

    return (
        <Card className="p-6 shadow-sm border-border/50">
            <h3 className="mb-4">{t('settings.accountInformation')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="firstName">{t('settings.firstName')}</Label>
                        <Input
                            id="firstName"
                            {...register("firstName", {
                                required: t('settings.firstNameRequired'),
                                minLength: {
                                    value: 2,
                                    message: t('settings.firstNameMinLength')
                                }
                            })}
                        />
                        {errors.firstName && (
                            <p className="text-sm text-destructive">{errors.firstName.message}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="lastName">{t('settings.lastName')}</Label>
                        <Input
                            id="lastName"
                            {...register("lastName", {
                                required: t('settings.lastNameRequired'),
                                minLength: {
                                    value: 2,
                                    message: t('settings.lastNameMinLength')
                                }
                            })}
                        />
                        {errors.lastName && (
                            <p className="text-sm text-destructive">{errors.lastName.message}</p>
                        )}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="email">{t('settings.emailAddress')}</Label>
                    <Input
                        id="email"
                        type="email"
                        {...register("email", {
                            required: t('settings.emailRequired'),
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('settings.invalidEmail')
                            }
                        })}
                    />
                    {errors.email && (
                        <p className="text-sm text-destructive">{errors.email.message}</p>
                    )}
                </div>
            </form>
            <Separator className="my-6"/>
            <div className="flex justify-end">
                <Button
                    type="submit"
                    disabled={isUpdating || isInitiating || !isDirty}
                    onClick={handleSubmit(onSubmit)}
                    className="rounded-full px-8"
                >
                    {isUpdating || isInitiating
                        ? t('settings.changingEmail')
                        : t('settings.saveChanges')}
                </Button>
            </div>

            <AlertDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('settings.emailChangeConfirm.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('settings.emailChangeConfirm.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isInitiating}>
                            {t('settings.emailChangeConfirm.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleInitiateEmailChange}
                            disabled={isInitiating}
                        >
                            {isInitiating ? t('settings.changingEmail') : t('settings.emailChangeConfirm.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
